/**
 * 일일상점 커맨드 (일감 S3b) — GameScene buyDailyShopSlot/fulfillDailyShopPurchase/rerollDailyShop 추출 (Phaser-free).
 * 통화 결제·광고 게이트·보상 해석/지급·sold-out·누진 리롤 결정만 — 모달/토스트는 셸 콜백(S6에서 재결선).
 * 최근 구매 결과 문구(lastResult)는 본 모듈이 소유 — buildDailyShopData가 getter로 읽는다.
 * 컨트롤러는 ctx getter 경유 (applySaveData 교체 stale 방지 규약).
 */
import { BigNumber } from "@yjsgames/bignumber";
import { t } from "../../i18n/i18n.js";
import { formatBig } from "../../ui/formatNumber.js";
import {
  dailyShopRewardSpec,
  dailyShopCostAmount,
  shouldSkipAd,
  type DailyShopPoolEntry,
} from "../../data/dailyShopData.js";
import { expandedModulePool } from "../../data/moduleDefinitions.js";
import {
  resolveRewards,
  type ResolvedReward,
} from "../../systems/RewardResolver.js";
import { formatRewardSummary } from "../../ui/tabData.js";
import type { GameContext } from "../GameContext.js";
import type { ModuleInstance } from "../../models/ModuleInstance.js";
import type { ShellPorts } from "./ports.js";
import { RewardService } from "./RewardService.js";
import { trackFirstPurchase } from "../../analytics/trackFirstPurchase.js";

export interface DailyShopCommandDeps {
  ctx: GameContext;
  ports: ShellPorts;
  rewards: RewardService;
  /** 광고 제거 entitlement — ad 슬롯 즉시 수령 분기 (SaveData.adFree). */
  adFree: () => boolean;
  /** 광고 처리 중 — UI가 ad 상품 버튼을 준비중 상태로 표시한다. */
  adProcessing?: () => boolean;
  /** 보상형 광고 Port 위임 — 시청 완료 시 onReward 콜백 (셸 rewardedAd 보유). */
  showRewardedAd: (onReward: () => void) => void;
  /** 잔액 부족 안내 토스트 — 셸 ToastPresenter 위임. */
  showToast: (message: string) => void;
  /**
   * 보상 획득 그리드 모달 — 모달 흐름이 셸 잔존이라 콜백 (S6에서 재결선).
   * instances = grant가 생성한 모듈 인스턴스(모달이 실제 조각 모양을 그리는 데 사용).
   */
  openRewardModal: (reward: ResolvedReward, instances?: readonly ModuleInstance[]) => void;
  /** 구매 성공 알림 — 가이드퀘스트 actionCompleted 목표 진행. */
  onActionCompleted?: (target: string) => void;
}

export class DailyShopCommands {
  /** 최근 구매/리롤 결과 문구 (현지화 완료). 빈 값이면 buildDailyShopData가 안내 문구로 대체. */
  private lastResult = "";

  constructor(private readonly deps: DailyShopCommandDeps) {}

  lastResultText(): string {
    return this.lastResult;
  }

  adProcessing(): boolean {
    return this.deps.adProcessing?.() ?? false;
  }

  /** 일일상점 슬롯 구매 — 통화 차감 → 보상 지급(resolveRewards) → sold-out. 잔액 부족 시 안내. */
  buySlot(index: number): void {
    const { ctx, ports } = this.deps;
    const slot = ctx.dailyShop().slotAt(index);
    const entry = ctx.dailyShop().slotEntry(index);
    if (!slot || !entry || slot.purchased) return;
    const bestChapter = ctx.chapter().bestChapter;

    if (entry.costCurrency === "ad") {
      // 광고 제거(adFree) 보유 시 광고 없이 즉시 수령, 미보유 시 보상형 광고(Port) 시청 완료 후 지급(DIP).
      if (this.adProcessing()) return;
      if (shouldSkipAd(entry.costCurrency, this.deps.adFree())) {
        this.fulfill(index, entry, bestChapter);
      } else {
        this.deps.showRewardedAd(() => this.fulfill(index, entry, bestChapter));
      }
      return;
    }

    const cost = dailyShopCostAmount(entry, slot.discountPercent, bestChapter);
    const paid =
      entry.costCurrency === "gold"
        ? ctx.economy().trySpend(cost)
        : ctx.economy().trySpendDiamond(cost);
    if (!paid) {
      const msg =
        entry.costCurrency === "gold"
          ? t("toast.goldShort", { cost: formatBig(cost) })
          : t("toast.diamondShort", { cost: formatBig(cost) });
      this.lastResult = msg;
      this.deps.showToast(msg);
      ports.invalidateUi();
      return;
    }
    this.fulfill(index, entry, bestChapter);
  }

  /** 슬롯 보상 지급 — 통화 결제 또는 광고 시청 완료 후 공통 (sound→보상→sold-out→저장). */
  private fulfill(
    index: number,
    entry: DailyShopPoolEntry,
    bestChapter: number,
  ): void {
    const { ctx, ports, rewards } = this.deps;
    ctx.soundController().play("box");
    const spec = dailyShopRewardSpec(entry, bestChapter);
    const reward = resolveRewards([spec], {
      rng: Math.random,
      rarityDefs: expandedModulePool(),
    });
    const insts = rewards.grant(reward);
    this.deps.openRewardModal(reward, insts);
    ctx.dailyShop().markPurchased(index);
    ctx.analytics().log("purchase", { type: "daily_shop" }); // 일일상점 구매 추적 (상자/IAP와 동형)
    trackFirstPurchase(ctx.analytics(), ctx.analyticsMilestones()); // 첫 구매 1회 퍼널 (funnel #9)
    this.deps.onActionCompleted?.("dailyShop.buy");
    this.lastResult =
      reward.moduleDrops.length > 0
        ? t("dailyShop.purchasedModule", {
            name: t(`module.${reward.moduleDrops[0]!.id}.name`),
          })
        : t("dailyShop.purchased", { reward: formatRewardSummary([spec]) });
    ports.invalidateUi();
    ports.persistNow(); // 일일상점 구매(다이아/광고 보상 수령) — 즉시 저장(유실 불가)
  }

  /** 일일상점 새로고침 — 무료 1회 또는 다이아 누진 차감 후 슬롯 재추첨. 소진/부족 시 무변경. */
  reroll(): void {
    const { ctx, ports } = this.deps;
    const cost = ctx.dailyShop().rerollCost();
    if (!cost) return; // 소진 — 내일 자정
    if (cost.kind === "diamond") {
      if (!ctx.economy().trySpendDiamond(BigNumber.fromNumber(cost.amount))) {
        const msg = t("toast.diamondShort", { cost: cost.amount });
        this.lastResult = msg;
        this.deps.showToast(msg);
        ports.invalidateUi();
        return;
      }
    }
    ctx.dailyShop().reroll();
    ctx.soundController().play("box");
    this.lastResult = "";
    ports.invalidateUi();
    ports.persistNow(); // 리롤(다이아 누진 소비) — 즉시 저장(유료 재화 유실 방지)
  }
}
